import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, MessageCircle, AlertCircle, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MobileNav } from "@/components/mobile-nav";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/router";
import type { InstagramProfile } from "@/lib/instagram-service";
import { CreditAlertDialog } from "@/components/credit-alert-dialog";
import { Persona, defaultPersonaId, getPersonaById } from "@/lib/personas";
import { useProfileLoader } from "@/hooks/use-profile-loader";
import { saveInstagramAccount } from "@/lib/saved-accounts-service";
import { ThemeToggle } from "@/components/theme-toggle";
import { Card, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ProfileImage } from "@/components/profile/profile-image";
import { ProfileStats } from "@/components/profile/profile-stats";
import { LoadingIndicator } from "@/components/profile/loading-indicator";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/lib/supabase";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { AiProfileConsentDialog } from "@/components/ai-profile-consent-dialog";

interface QuotaExceededDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlan?: string;
}

function QuotaExceededDialog({
  open,
  onOpenChange,
  currentPlan = "free",
}: QuotaExceededDialogProps) {
  const navigate = useNavigate();

  const planDetails = {
    free: {
      title: "무료 계정 제한",
      limit: "2개",
      message:
        "무료 계정은 두 개의 인스타그램 계정만 연결할 수 있습니다. 더 많은 계정을 연결하려면 유료 구독이 필요합니다.",
    },
    basic: {
      title: "베이직 플랜 제한",
      limit: "10개",
      message:
        "베이직 구독은 10개의 인스타그램 계정을 연결할 수 있습니다. 더 많은 계정을 연결하려면 프리미엄 구독으로 업그레이드하세요.",
    },
    premium: {
      title: "계정 제한 오류",
      limit: "무제한",
      message:
        "프리미엄 구독에서는 계정 제한이 없어야 합니다. 이 오류가 계속되면 고객 지원에 문의하세요.",
    },
  };

  const details =
    planDetails[currentPlan as keyof typeof planDetails] || planDetails.free;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md md:max-w-md lg:max-w-md">
        <AlertDialogHeader className="mb-4 space-y-2">
          <AlertDialogTitle className="flex items-center text-xl md:text-2xl text-destructive gap-3">
            <AlertCircle className="h-6 w-6 flex-shrink-0" />
            <span>{details.title}</span>
          </AlertDialogTitle>
          <AlertDialogDescription className="text-base leading-relaxed">
            현재 인스타그램 계정 연결 제한인 {details.limit}에 도달했습니다.
            <br className="hidden md:block" />
            {details.message}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-4 mt-6 md:mt-8 pt-2">
          <AlertDialogCancel className="mt-2 sm:mt-0 h-12 text-base">
            닫기
          </AlertDialogCancel>
          <Button
            className="gradient-button w-full sm:w-auto h-12 text-base font-medium"
            onClick={() => {
              onOpenChange(false);
              navigate("/pricing");
            }}
          >
            <Zap className="mr-2 h-5 w-5" />
            요금제 보기
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// 프로필 데이터 인터페이스 정의
type ProfileData = InstagramProfile;

interface ProfilePreviewProps {
  username: string;
  onContinue: (profile: ProfileData) => void;
  onBack: () => void;
}

export function ProfilePreview({
  username,
  onContinue,
  onBack,
}: ProfilePreviewProps) {
  const [showCreditAlert, setShowCreditAlert] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showQuotaExceededDialog, setShowQuotaExceededDialog] = useState(false);
  const [showAiProfileDialog, setShowAiProfileDialog] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<string>("free");
  const { session } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Always use the default persona ('secretly-deviant')
  const basePersona = getPersonaById(defaultPersonaId);
  const selectedPersona: Persona = {
    ...basePersona,
    systemMessage: basePersona.systemMessage.replace(/{username}/g, username),
  };

  // Custom hook to handle profile loading logic
  const { profile, isLoading, loadingProgress, loadingStage, error } =
    useProfileLoader({
      username,
      onBack,
    });

  // Handle chat start
  const handleChatStart = async () => {
    if (!session) {
      toast({
        title: "로그인 필요",
        description: "대화하려면 로그인이 필요합니다.",
      });
      navigate("/auth");
      return;
    }

    if (!profile) {
      toast({
        title: "오류",
        description: "프로필 정보가 없습니다. 다시 로드해주세요.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      // 사용자가 무제한 구독이 아닌 경우에만 계정 쿼터 확인
      const { data: quotaData, error: quotaError } = await supabase
        .from("user_chat_credits")
        .select("account_quota")
        .eq("user_id", session.user.id)
        .single();

      if (!quotaError && quotaData && quotaData.account_quota !== -1) {
        // 현재 연결된 계정 수 확인
        const { count, error: countError } = await supabase
          .from("saved_instagram_accounts")
          .select("*", { count: "exact", head: true })
          .eq("user_id", session.user.id)
          .is("deleted_at", null);

        // 쿼터 초과 시 다이얼로그 표시
        if (!countError && count !== null && count >= quotaData.account_quota) {
          toast({
            title: "계정 연결 한도 도달",
            description: `현재 구독으로는 최대 ${quotaData.account_quota}개의 계정만 연결할 수 있습니다. 계정을 추가하려면 업그레이드가 필요합니다.`,
            variant: "destructive",
          });
          setCurrentPlan(quotaData.account_quota === 10 ? "basic" : "free");
          setShowQuotaExceededDialog(true);
          setIsSaving(false);
          return;
        }
      }

      // 계정 저장 시도 - 페르소나 정보 함께 저장
      console.log("인스타그램 계정 저장 시도:", username);
      const saveResult = await saveInstagramAccount(
        username,
        profile,
        selectedPersona,
        profile?.profilePicUrl,
        profile?.profilePicUrlHD
      );

      if (saveResult.success) {
        console.log("계정 저장 성공:", username);
      } else {
        console.log("계정 저장 문제:", saveResult.message);

        // 계정 제한 초과 메시지 처리
        if (saveResult.quotaExceeded) {
          toast({
            title: "계정 연결 한도 도달",
            description: `현재 구독으로는 최대 ${saveResult.currentPlan === 'basic' ? '10' : '2'}개의 계정만 연결할 수 있습니다. 계정을 추가하려면 업그레이드가 필요합니다.`,
            variant: "destructive",
          });
          setCurrentPlan(saveResult.currentPlan || "free");
          setShowQuotaExceededDialog(true);
          setIsSaving(false);
          return;
        }
      }

      // 대화 시작
      onContinue(profile);
    } catch (error) {
      console.error("계정 저장 오류:", error);
      toast({
        title: "계정 저장 실패",
        description: error instanceof Error ? error.message : "계정 저장 중 오류가 발생했습니다.",
        variant: "destructive",
      });
      // 실패해도 대화 계속
      onContinue(profile);
    } finally {
      setIsSaving(false);
    }
  };

 

  // 프로필 컨텐츠에 스켈레톤 로딩 상태를 렌더링하는 함수
  const renderProfileSkeleton = () => {
    return (
      <div className="w-full max-w-4xl animate-fade-in space-y-8">
        {/* Profile View Skeleton */}
        <div className="rounded-xl border bg-card p-6 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-start md:gap-8">
            {/* Profile Image Skeleton */}
            <div className="mb-6 flex flex-col items-center md:mb-0">
              <Skeleton className="h-24 w-24 rounded-full mb-4 ring-4 ring-primary/10" />

              <div className="flex w-full justify-center gap-2">
                <Skeleton className="h-6 w-12 rounded-full" />
              </div>
            </div>

            {/* Profile Info Skeleton */}
            <div className="flex-1 space-y-4">
              <div className="space-y-1">
                <Skeleton className="h-8 w-36" />
                <Skeleton className="h-4 w-24" />
              </div>

              <div className="grid w-full grid-cols-3 gap-4">
                <Skeleton className="h-16 rounded-md" />
                <Skeleton className="h-16 rounded-md" />
                <Skeleton className="h-16 rounded-md" />
              </div>

              <div className="mt-4">
                <Skeleton className="h-4 w-24 mb-1" />
                <Skeleton className="h-24 w-full rounded-md" />
              </div>
            </div>
          </div>
        </div>

        {/* Start Chat */}
        <Card className="overflow-hidden border shadow-lg">
          <CardFooter className="flex flex-col space-y-3 bg-muted/10 p-6">
            <Button
              className="w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 py-4 sm:py-6 text-xs sm:text-sm"
              disabled={true}
            >
              <span className="whitespace-normal text-center">
                AI 프로필 사진 생성
              </span>
            </Button>

            <Button
              className="w-full gradient-button py-4 sm:py-6 text-xs sm:text-sm"
              disabled={true}
            >
              프로필 로딩 중...
            </Button>

            <Button variant="outline" onClick={onBack} className="w-full text-xs sm:text-sm py-4 sm:py-6">
              다시 검색하기
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  };

  return (
    <div className="pb-24 flex min-h-screen flex-col bg-gradient-to-br from-background to-background/80">
      <div className="absolute inset-0 grid-pattern opacity-20"></div>

      <header className="container sticky top-0 z-30 border-b bg-background/80 backdrop-blur-lg">
        <div className="flex h-14 sm:h-16 items-center justify-between">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="mr-1 sm:mr-2"
            >
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="sr-only">뒤로 가기</span>
            </Button>
            <img
              src="https://storage.googleapis.com/glide-prod.appspot.com/uploads-v2/FYU7LsqDXg9yUwI6tiLP/pub/lXbB8YOdvvz5IcMbcvsM.png"
              alt="INSTA LAB AI Logo"
              className="h-6 w-6 sm:h-7 sm:w-7 object-contain"
            />
            <h1 className="text-lg sm:text-xl font-bold">INSTA LAB AI</h1>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="container flex-1 flex-col items-center pb-20 sm:pb-24 pt-4 sm:pt-6">
        {/* Loading State */}
        {isLoading ? (
          <LoadingIndicator
            progress={loadingProgress}
            stage={loadingStage}
            error={error || undefined}
          />
        ) : error ? (
          // Error state
          <LoadingIndicator
            progress={loadingProgress}
            stage={loadingStage}
            error={error}
          />
        ) : !profile ? (
          // If not loading but profile is not yet available, show skeleton
          renderProfileSkeleton()
        ) : (
          <div className="w-full max-w-4xl animate-fade-in space-y-8">
            {/* Profile View */}
            {profile && (
              <div className="rounded-xl border bg-card p-6 shadow-lg">
                <div className="flex flex-col md:flex-row md:items-start md:gap-8">
                  {/* Profile Image (Left Section) */}
                  <div className="mb-5 sm:mb-6 flex flex-col items-center md:mb-0">
                    <ProfileImage
                      src={profile.profilePicUrlHD || profile.profilePicUrl}
                      username={profile.username}
                      size="lg"
                      className="mb-3 sm:mb-4 ring-4 ring-primary/10"
                      onRefresh={() => {
                        // Refresh profile image
                        toast({
                          title: "이미지 새로고침",
                          description: "프로필 이미지를 새로고침했습니다.",
                        });
                      }}
                    />

                    <div className="flex w-full justify-center gap-1.5 sm:gap-2">
                      {profile.isVerified && (
                        <Badge className="bg-blue-500 text-[10px] sm:text-xs py-0.5 px-1.5 sm:px-2">인증됨</Badge>
                      )}
                      {profile.isBusinessAccount && (
                        <Badge variant="outline" className="text-[10px] sm:text-xs py-0.5 px-1.5 sm:px-2">비즈니스</Badge>
                      )}
                    </div>
                  </div>

                  {/* Profile Info (Right Section) */}
                  <div className="flex-1 space-y-3 sm:space-y-4">
                    <div className="space-y-1">
                      <h2 className="text-xl sm:text-2xl font-bold">
                        @{profile.username}
                      </h2>
                      {profile.fullName && (
                        <p className="text-base sm:text-lg text-muted-foreground">
                          {profile.fullName}
                        </p>
                      )}
                    </div>

                    <ProfileStats
                      postsCount={profile.postsCount}
                      followersCount={profile.followersCount}
                      followsCount={profile.followsCount}
                    />

                    {profile.biography && (
                      <div className="mt-3 sm:mt-4">
                        <h3 className="text-xs sm:text-sm font-medium text-muted-foreground mb-1">
                          소개
                        </h3>
                        <p className="text-xs sm:text-sm whitespace-pre-wrap bg-muted/30 p-2.5 sm:p-3 rounded-md">
                          {profile.biography}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 대화하기/다시 검색하기 버튼 - 최근 게시물 위로 이동 */}
            <Card className="overflow-hidden border shadow-lg">
              <CardFooter className="flex flex-col space-y-2.5 sm:space-y-3 bg-muted/10 p-4 sm:p-6">
               

                <Button
                  onClick={handleChatStart}
                  id="start-chat-button"
                  disabled={isSaving || !profile}
                  className="w-full flex items-center justify-center gap-1.5 sm:gap-2 gradient-button py-3 sm:py-4 text-xs sm:text-sm transition-transform hover:scale-105 active:scale-95"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" />
                      <span className="whitespace-normal text-center">채팅 준비 중...</span>
                    </>
                  ) : !profile ? (
                    <span className="whitespace-normal text-center">프로필 로딩 중...</span>
                  ) : (
                    <>
                      <MessageCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-pulse" />
                      <span className="whitespace-normal text-center">
                        {username}와 대화하기
                      </span>
                    </>
                  )}
                </Button>

                <Button
                  variant="outline"
                  onClick={onBack}
                  className="w-full text-xs sm:text-sm py-3 sm:py-4"
                >
                  다시 검색하기
                </Button>
              </CardFooter>
            </Card>

            {/* 최근 게시물 섹션 - 버튼 아래로 이동 */}
            {profile && profile.latestPosts?.length > 0 && (
              <div className="rounded-xl border bg-card p-6 shadow-lg">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">
                  최근 게시물 ({profile.latestPosts.length}개)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-1">
                  {profile.latestPosts.map((post: InstagramProfile['latestPosts'][0]) => (
                    <div key={post.id} className="relative rounded-md overflow-hidden shadow-sm group">
                      <div className="w-full">
                        <img
                          src={post.displayUrl}
                          alt={`@${profile.username}의 게시물`}
                          className="w-full h-auto object-contain bg-muted/10 hover:bg-muted/30 transition-colors"
                          loading="lazy"
                        />
                      </div>
                      {post.type === 'video' && (
                        <div className="absolute top-2 right-2 bg-black/70 rounded-full p-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                            <polygon points="5 3 19 12 5 21 5 3"></polygon>
                          </svg>
                        </div>
                      )}

                      {/* 게시물 정보 오버레이 */}
                      <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        {/* 캡션 영역 */}
                        {post.caption && (
                          <div className="px-3 pt-3 pb-8">
                            <p className="text-xs text-white line-clamp-3 font-medium">{post.caption}</p>
                          </div>
                        )}

                        {/* 좋아요/댓글 정보 */}
                        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-white text-xs z-10">
                          <div className="flex gap-2">
                            <span className="flex items-center gap-1 bg-black/60 rounded-full px-2 py-1 backdrop-blur-sm">
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                              </svg>
                              {post.likesCount}
                            </span>
                            <span className="flex items-center gap-1 bg-black/60 rounded-full px-2 py-1 backdrop-blur-sm">
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                              </svg>
                              {post.commentsCount}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* 항상 표시되는 좋아요/댓글 정보 (비호버 상태) */}
                      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-white text-xs group-hover:opacity-0 transition-opacity duration-200">
                        <div className="flex gap-2">
                          <span className="flex items-center gap-1 bg-black/60 rounded-full px-2 py-1 backdrop-blur-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                            </svg>
                            {post.likesCount}
                          </span>
                          <span className="flex items-center gap-1 bg-black/60 rounded-full px-2 py-1 backdrop-blur-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                            </svg>
                            {post.commentsCount}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <MobileNav />

      <CreditAlertDialog
        open={showCreditAlert}
        onOpenChange={setShowCreditAlert}
        showPricingCards={true}
      />

      <QuotaExceededDialog
        open={showQuotaExceededDialog}
        onOpenChange={setShowQuotaExceededDialog}
        currentPlan={currentPlan}
      />

      <AiProfileConsentDialog
        open={showAiProfileDialog}
        onOpenChange={setShowAiProfileDialog}
        username={username}
        profileData={profile}
      />
    </div>
  );
}